import * as React from 'react';

import { ButtonGroup, HStack, IconButton, Input, InputGroup, InputRightElement, Select, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, chakra } from '@chakra-ui/react';
import {
    ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiSearch } from 'react-icons/fi';
import { PiFileCsv, PiFilePdf } from 'react-icons/pi';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';

export type DataTableProps<Data extends object> = {
    data: Data[];
    columns: ColumnDef<Data, any>[];
    showPagination?: boolean;
    variant?: 'simple' | 'striped' | 'unstyled';
    size?: 'sm' | 'md' | 'lg';
    showSearch?: boolean;
};

export function DataTable<Data extends object>({
    data,
    columns,
    showPagination = true,
    variant = 'simple',
    size = 'md',
    showSearch = true
}: DataTableProps<Data>) {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: {
            sorting
        }
    });

    return (
        <>
            {
                showSearch &&
                <HStack mb={4} justifyContent={'space-between'}>
                    <InputGroup maxW={{ base: '100%', md: 300 }}>
                        <InputRightElement pointerEvents='none'>
                            <FiSearch color='#9e9e9e' />
                        </InputRightElement>
                        <Input
                            type='text'
                            placeholder='Search'
                            _focusVisible={{
                                boxShadow: '0 0 0 1px #ffc107'
                            }}
                        />
                    </InputGroup>
                    <HStack>
                        <Text>Exports: </Text>
                        <ButtonGroup size='sm' isAttached variant='outline'>
                            <IconButton aria-label='Add to friends' icon={<PiFilePdf />} />
                            <IconButton aria-label='Add to friends' icon={<PiFileCsv />} />
                        </ButtonGroup>
                    </HStack>
                </HStack>
            }
            <TableContainer>
                <Table variant={variant} size={size}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                                    const meta: any = header.column.columnDef.meta;
                                    return (
                                        <Th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            isNumeric={meta?.isNumeric}
                                            whiteSpace='nowrap'
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}

                                            <chakra.span pl='4'>
                                                {header.column.getIsSorted() ? (
                                                    header.column.getIsSorted() === 'desc' ? (
                                                        <TriangleDownIcon aria-label='sorted descending' />
                                                    ) : (
                                                        <TriangleUpIcon aria-label='sorted ascending' />
                                                    )
                                                ) : null}
                                            </chakra.span>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => (
                            <Tr key={row.id}>
                                {row.getVisibleCells().map((cell) => {
                                    // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                                    const meta: any = cell.column.columnDef.meta;
                                    return (
                                        <Td key={cell.id} isNumeric={meta?.isNumeric} whiteSpace='nowrap'>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
            {
                showPagination && table.getRowModel().rows.length != 0 &&
                <HStack mt={3}>
                    <IconButton
                        size={'sm'}
                        aria-label='back chevron'
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                        icon={<FiChevronsLeft />}
                        colorScheme='yellow'
                    />
                    <IconButton
                        size={'sm'}
                        aria-label='back chevron'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        icon={<FiChevronLeft />}
                        colorScheme='yellow'
                        variant={'outline'}
                    />
                    <Text whiteSpace={'nowrap'}>
                        {table.getState().pagination.pageIndex + 1} of{' '} {table.getPageCount()}
                    </Text>
                    <IconButton
                        size={'sm'}
                        aria-label='back chevron'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        icon={<FiChevronRight />}
                        colorScheme='yellow'
                        variant={'outline'}
                    />
                    <IconButton
                        size={'sm'}
                        aria-label='back chevron'
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                        icon={<FiChevronsRight />}
                        colorScheme='yellow'
                    />
                    <span className='flex items-center gap-1'>
                        | Go to page:
                        <Input
                            type='number'
                            defaultValue={table.getState().pagination.pageIndex + 1}
                            onChange={e => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            placeholder='Basic usage'
                            maxW={50}
                            size='sm'
                            _focusVisible={{
                                boxShadow: '0 0 0 1px #ffc107'
                            }}
                        />
                    </span>
                    <Select
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                            table.setPageSize(Number(e.target.value));
                        }}
                        size='sm'
                        maxW={110}
                        _focusVisible={{
                            boxShadow: '0 0 0 1px #ffc107'
                        }}
                    >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </Select>
                </HStack>
            }
        </>
    );
}